import "./list-campaign.scss";

import { GridItemModel } from "@core/ui/grid-item/grid-item";
import TagColor from "@core/ui/tag-color/tag-color";
import { useSphereRedeemItemCtx } from "@domain/reward/sphere-redeem-item/sphere-redeem-item.context";
import {
  GetCampaignModel,
  GetResponseCampaignModel,
} from "@factory/model/query-model-campaign";
import { useQyGetCampaign } from "@factory/query/campaign.query";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import TextBlock from "@shared/ui/text-block/text-block";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import classNames from "classnames";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { DataView } from "primereact/dataview";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { Skeleton } from "primereact/skeleton";
import { useEffect, useRef, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { Outlet, useNavigate } from "react-router-dom";

import { appFeature } from "../../../app.feature";
import { FilterCampaign } from "../filter-campaign/filter-campaign";
import { useFilterCampaignContext } from "../filter-campaign/filter-campaign.context";
import {
  resolveCampaignImageBlob,
  rowsFromCampaignApiResponse,
} from "./list-campaign.logic";

export function ListCampaign() {
  // THIS IS THE LOCAL DECLARATION
  const { pointsText, stampsText } = appFeature.config.label;
  const { setSelectedRedeemCampaign } = useSphereRedeemItemCtx();
  const { formatDateLocal } = useDateTimeContext();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [sort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterCampaignContext();
  const [responseData, setResponseData] = useState<GetCampaignModel[]>([]);
  const [imageBlobByCode, setImageBlobByCode] = useState<
    Record<string, string | null>
  >({});
  const imageBlobUrlsRef = useRef<string[]>([]);

  // THIS IS THE QUERY DECLARATION
  const handleGetCampaignSuccess = (response: GetResponseCampaignModel) => {
    setResponseData(rowsFromCampaignApiResponse(response));
  };

  useEffect(() => {
    let cancelled = false;

    imageBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    imageBlobUrlsRef.current = [];
    setImageBlobByCode({});

    responseData.forEach((element) => {
      resolveCampaignImageBlob(element, viewAsset).then((result) => {
        if (cancelled || !result) {
          return;
        }
        if (result.blobUrl) {
          imageBlobUrlsRef.current.push(result.blobUrl);
        }
        setImageBlobByCode((prev) => ({
          ...prev,
          [result.code]: result.blobUrl,
        }));
      });
    });

    return () => {
      cancelled = true;
      imageBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      imageBlobUrlsRef.current = [];
    };
  }, [responseData]);
  const {
    data: campaignResponse,
    isLoading,
    isError,
    error,
  } = useQyGetCampaign(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues(),
    true,
    handleGetCampaignSuccess
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleRedeemDialog = (item: GetCampaignModel) => {
    const selectedCampaign = GridItemModel.ConvertByCampaign(item);
    setSelectedRedeemCampaign(selectedCampaign);
    navigate(`redeem/${item.code}`);
  };
  const navigateToRecord = (item: GetCampaignModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };
  const handleRedeemRedirect = () => {
    navigate("redeem-code");
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterCampaign />
      </Sidebar>
    </section>
  );
  const itemTemplate = (cell: any) => {
    return (
      <div
        className="col-12 border-b border-b-gray-300 p-5 hover:bg-gray-100"
        key={cell.code}
      >
        <section className="flex justify-between border border-dashed border-gray-200 px-4 py-2 w-full mb-2">
          <div className="flex gap-4">
            <div className="flex gap-2 items-center justify-center">
              <span>Redeem By {pointsText}</span>
              <TagColor value={cell.redeem_by_points} />
            </div>
            <div className="flex gap-2 items-center justify-center">
              <span>Redeem By {stampsText}</span>
              <TagColor value={cell.redeem_by_stamp} />
            </div>
            <div className="flex gap-2 items-center justify-center">
              <span>Continuous Redemption</span>
              <TagColor value={cell.multi_redeem} />
            </div>
            <div className="flex gap-2 items-center justify-center">
              <span>Criteria</span>
              <TagColor value={cell.constraint} />
            </div>
            <div className="flex gap-2 items-center justify-center">
              <span>Total Redeem:</span>
              <b>{cell.total_redeem_count}</b>
            </div>
          </div>
          <div className="flex gap-2 items-center justify-center">
            <TextBlock label={pointsText} value={cell.points} />
            <TextBlock label={stampsText} value={cell.stamp} />
          </div>
        </section>

        <div className={classNames("flex w-full gap-4 justify-between")}>
          <section className="flex gap-4 items-center">
            {cell.image && imageBlobByCode[cell.code] === undefined ? (
              <Skeleton
                width="150px"
                height="150px"
                className="flex-shrink-0"
              />
            ) : (
              <Avatar
                className="flex-shrink-0"
                image={imageBlobByCode[cell.code] ?? undefined}
                style={{ width: "150px", height: "150px" }}
              />
            )}
            <div className="flex flex-col">
              <div>
                <h4>{cell.name}</h4>
                <p>{cell.description}</p>
                <TextBlock label={"Level"} value={cell.level_name} />
                <TextBlock
                  label={"Duration"}
                  value={`${formatDateLocal(
                    cell.start_date
                  )} to ${formatDateLocal(cell.end_date)}`}
                />
              </div>
            </div>
          </section>
          <section className="flex flex-col gap-2 items-center justify-center">
            <Button
              label="View"
              outlined
              severity="secondary"
              className="block w-full"
              onClick={() => navigateToRecord(cell)}
            />
            {/* <Button
              label="Redeem"
              className="block w-full"
              onClick={() => handleRedeemDialog(cell)}
            /> */}
          </section>
        </div>
      </div>
    );
  };

  const listTemplate = (items: GetCampaignModel[]): any => {
    if (!items || items.length === 0) return undefined;

    let list = items.map((product) => {
      return itemTemplate(product);
    });

    return <div className="grid grid-nogutter">{list}</div>;
  };

  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{campaignResponse?.count}</b>
      </h5>

      <DataView
        value={responseData}
        listTemplate={listTemplate}
        paginator
        rows={rowLimit}
        first={first}
        totalRecords={campaignResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPage={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListCampaign" className="campaign">
      <HeaderContent title={<h1>Campaign</h1>}>
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
          <Button label="Redeem" onClick={handleRedeemRedirect} outlined />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
      <Outlet />
    </div>
  );
}

export default ListCampaign;
